import type { AppearanceSettings } from '@/state/appearance-settings';

type Branding = AppearanceSettings['branding'];

export const STOCK = {
  background: '#f4f7f3',
  button: '#F97066',
  heading_text: '#181D27',
  body_text: '#414651',
  muted_text: '#717680',
};

// Studio-authored, and the result is rendered as CSS text rather than handed to
// React's style prop, so anything that is not plainly a colour is dropped
// instead of escaped. A value carrying a brace closes the rule it sits in. The
// admin's picker only ever emits hex.
const HEX_COLOR = /^#[0-9a-f]{3,8}$/i;

const color = (value: string | undefined, stock: string): string =>
  value && HEX_COLOR.test(value.trim()) ? value.trim() : stock;

// The six lowercase digits of a colour CSS would paint, alpha dropped. Five and
// seven digits pass HEX_COLOR but name no colour, so they answer null.
export const opaqueHex = (value: string | undefined): string | null => {
  const trimmed = value?.trim();
  if (!trimmed || !HEX_COLOR.test(trimmed)) return null;
  const digits = trimmed.slice(1).toLowerCase();
  if (digits.length === 3 || digits.length === 4) {
    return [...digits.slice(0, 3)].map((digit) => digit + digit).join('');
  }
  if (digits.length === 6 || digits.length === 8) return digits.slice(0, 6);
  return null;
};

type BasePalette = {
  background: string;
  button: string;
  heading: string;
  body: string;
  muted: string;
};

type Tint = { stop: number; percent: number; fallback: string };
type Shade = {
  stop: number;
  lightness: number;
  chroma: number;
  hue: number;
  black: number;
  fallback: string;
};

// Light red stops mix the button color toward literal white. The percentages
// are calibrated so the stock #F97066 lands within a step of the stock tints,
// which is why the plain fallbacks below are simply the stock hexes.
const RED_TINTS: Tint[] = [
  { stop: 25, percent: 3, fallback: '#FFFBFA' },
  { stop: 50, percent: 8, fallback: '#FEF3F2' },
  { stop: 100, percent: 18, fallback: '#FEE4E2' },
  { stop: 200, percent: 35, fallback: '#FECDCA' },
  { stop: 300, percent: 64, fallback: '#FDA29B' },
];

// Darker stops step down in oklch (lightness down, chroma up) like a designed
// ramp, calibrated so #F97066 lands on the stock 500 to 800 hexes. `black` is
// the color-mix tier for engines without relative color syntax: mixing toward
// black reaches the same lightness but desaturates, so it is a fallback, not
// the design. 800 is the darkest stop the library paints on a brand tint (the
// red Badge text). 900 and 950 stay stock, which is also what keeps the error
// Alert readable: the library puts text-red-900 on nodes inside the Alert that
// a root className cannot reach, and red-900 left stock is the same #7A271A as
// error-900.
const RED_SHADES: Shade[] = [
  {
    stop: 500,
    lightness: 0.072,
    chroma: 1.24,
    hue: 2,
    black: 14,
    fallback: '#F04438',
  },
  {
    stop: 600,
    lightness: 0.133,
    chroma: 1.23,
    hue: 3,
    black: 25,
    fallback: '#D92D20',
  },
  {
    stop: 700,
    lightness: 0.208,
    chroma: 1.07,
    hue: 3,
    black: 38,
    fallback: '#B42318',
  },
  {
    stop: 800,
    lightness: 0.276,
    chroma: 0.88,
    hue: 3,
    black: 50,
    fallback: '#912018',
  },
];

// The neutral ramp mixes the muted text toward white (percentages tuned so the
// default #717680 lands on the stock grays): 25-300 are surfaces and borders on
// white, 400-450 fade the muted text toward it. 450 keeps unavailable dates at
// 3.1:1 on white, where 400 sits at 2.4:1.
const GRAY_TINTS: Tint[] = [
  { stop: 25, percent: 1.5, fallback: '#FDFDFD' },
  { stop: 50, percent: 3.5, fallback: '#FAFAFA' },
  { stop: 100, percent: 7, fallback: '#F5F5F5' },
  { stop: 150, percent: 11, fallback: '#EFF0F1' },
  { stop: 200, percent: 15, fallback: '#E9EAEB' },
  { stop: 300, percent: 29, fallback: '#D5D7DA' },
  { stop: 400, percent: 64, fallback: '#A4A7AE' },
  { stop: 450, percent: 80, fallback: '#8D9199' },
];

// gray-600 sits between the muted and body text. The heading color drives 900
// only, so a tenant heading never bleeds into secondary text.
const GRAY_600_FALLBACK = '#535862';

// The components library bakes the stock palette into values the Tailwind
// theme cannot reach: the reds drive form-control states (toggle, checkbox and
// radio fills and hovers, input focus ring) and the link hover, the grays drive control
// borders, focus-ring halos, skeleton shimmer and scrollbars. Aliasing them
// onto the brand vars keeps one set of formulas. --mb-red-500 is deliberately
// left off: the library reads it only for the error border on .mb-input and
// .mb-dd-trigger, so keeping it on the library's stock value is what makes an
// invalid input keep the stock error red.
const LIBRARY_ALIASES: Record<string, string> = {
  '--mb-red-25': 'var(--brand-red-25)',
  '--mb-red-100': 'var(--brand-red-100)',
  '--mb-red-200': 'var(--brand-red-200)',
  '--mb-red-300': 'var(--brand-red-300)',
  '--mb-red-400': 'var(--brand-button)',
  '--mb-red-600': 'var(--brand-red-600)',
  '--mb-focus-color': 'var(--brand-button)',
  '--mb-gray-100': 'var(--brand-gray-100)',
  '--mb-gray-200': 'var(--brand-gray-200)',
  '--mb-gray-300': 'var(--brand-gray-300)',
  '--mb-gray-400': 'var(--brand-gray-400)',
  '--mb-gray-500': 'var(--brand-muted)',
};

// A step off the page background for a surface on a white card: darker on a
// light background, lighter on a dark one, where a darker step would vanish.
// Worked out here from the hex rather than in CSS, since CSS has no way to pick
// the direction. 4.5% black takes the stock #f4f7f3 to within a step of the
// preset olive-50 (#e9eee7), which is what makes that the fallback. The row's
// text is the card's heading colour, so a shade that text cannot be read on
// (mid and dark backgrounds) gives way to the card's own gray-100.
const BG_SHADE_BLACK_PERCENT = 4.5;
const BG_SHADE_WHITE_PERCENT = 8;
// Roughly the perceptual middle (L* 50).
const DARK_BG_LUMINANCE = 0.18;
const MIN_TEXT_CONTRAST = 4.5;

// The hover stops step darker off the button, and on a near-black button a
// darker step is no step at all (#111111 lands on black), so a dark button
// hovers lighter instead: white mixed in at the percentages that give the same
// contrast against the button as the stock #F97066 gets from its own 500 (card
// hover) and 600 (button hover). Around 0.05 both directions are about as
// visible, so a colour either side of it shows no jump. 700 and 800 keep
// stepping darker, since they are text on the brand tints.
const DARK_BUTTON_LUMINANCE = 0.05;
const DARK_BUTTON_HOVER_WHITE = [
  { stop: 500, percent: 10 },
  { stop: 600, percent: 17 },
];

// Text straight on the page background is painted in the card text colours,
// which a dark background swallows. A tier whose studio colour reads under
// MIN_TEXT_CONTRAST there is raised: the background mixed toward white from
// `percent` up until it reaches `target`, else white. A tier that reads keeps
// the studio's colour, and a dark background all three read on gets none of
// the ground vars, so its rule stays as it was.
const GROUND_TIERS = [
  { tier: 'heading', percent: 94, target: 7 },
  { tier: 'body', percent: 78, target: MIN_TEXT_CONTRAST },
  { tier: 'muted', percent: 60, target: MIN_TEXT_CONTRAST },
] as const;

type GroundTier = (typeof GROUND_TIERS)[number]['tier'];

// The stock red-300, raised the same way: the red-600 error text sits under
// 4.5:1 on every dark background.
const GROUND_ERROR = [253, 162, 155];

// A tertiary button on the background hovers and presses onto the background
// lifted toward white, where on a card it would take gray-100 and gray-200.
const GROUND_TERTIARY_FILLS = [
  { state: 'hover', percent: 12 },
  { state: 'press', percent: 20 },
];

// Under 2:1 a white label all but disappears on the button, which then takes
// the stock dark. The app icon's monogram letter turns dark at the same cutoff.
const PALE_BUTTON_CONTRAST = 2;
const WHITE = [255, 255, 255];
const PALE_INK = '#181D27';

// A pale button hovers darker, in plain hexes for every engine: the ink reads
// at 5.8:1 or more on the button and on both stops.
const PALE_HOVER_BLACK = [
  { stop: 500, percent: 10 },
  { stop: 600, percent: 17 },
];

// A pale button's own tints wash out and read as disabled, so its light stops
// take the studio's gray ramp one step firmer.
const PALE_TINT_GRAYS = [
  { stop: 25, gray: 50 },
  { stop: 50, gray: 100 },
  { stop: 100, gray: 150 },
  { stop: 200, gray: 200 },
];

// Brand text on a pale brand is measured on gray-150, the firmest tint it sits
// on. Measured on white, darkened lime reads 4.5:1 there and only 4.15:1 on
// the available days' gray-100.
const BRAND_TEXT_SURFACE_GRAY = 150;
// 700 and 800 are the text the library sets on its own tints (the selected
// range's days, the red Badge), so they hold 7:1 on white.
const BRAND_TEXT_DEEP_CONTRAST = 7;
const OKLAB_LIGHTNESS_STEP = 0.001;
const COARSE_STEPS = 10;
const CHROMA_PRECISION = 1e-4;

// Six lowercase digits, as opaqueHex answers.
export const rgbOf = (hex: string): number[] =>
  [0, 2, 4].map((index) => parseInt(hex.slice(index, index + 2), 16));

export const linearChannel = (value: number) => {
  const channel = value / 255;
  return channel <= 0.04045
    ? channel / 12.92
    : ((channel + 0.055) / 1.055) ** 2.4;
};

const luminance = ([red, green, blue]: number[]) =>
  0.2126 * linearChannel(red) +
  0.7152 * linearChannel(green) +
  0.0722 * linearChannel(blue);

export const contrast = (a: number[], b: number[]) => {
  const [lighter, darker] = [luminance(a), luminance(b)].sort((x, y) => y - x);
  return (lighter + 0.05) / (darker + 0.05);
};

// Takes opaqueHex's answer, never the studio's own spelling: rgbOf reads no
// hash and no short form.
export const isPaleButton = (buttonHex: string) =>
  contrast(rgbOf(buttonHex), WHITE) < PALE_BUTTON_CONTRAST;

const mixToward = (rgb: number[], target: number, percent: number) =>
  rgb.map((value) => Math.round(value + ((target - value) * percent) / 100));

const hexOf = (rgb: number[]) =>
  `#${rgb.map((value) => value.toString(16).padStart(2, '0')).join('')}`;

const oklabOf = (rgb: number[]): number[] => {
  const [red, green, blue] = rgb.map(linearChannel);
  const [l, m, s] = [
    0.4122214708 * red + 0.5363325363 * green + 0.0514459929 * blue,
    0.2119034982 * red + 0.6806995451 * green + 0.1073969566 * blue,
    0.0883024619 * red + 0.2817188376 * green + 0.6299787005 * blue,
  ].map(Math.cbrt);
  return [
    0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s,
    1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s,
    0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s,
  ];
};

// Linear sRGB, outside 0 to 1 where sRGB cannot show the colour.
const linearOf = ([lightness, a, b]: number[]): number[] => {
  const [l, m, s] = [
    lightness + 0.3963377774 * a + 0.2158037573 * b,
    lightness - 0.1055613458 * a - 0.0638541728 * b,
    lightness - 0.0894841775 * a - 1.291485548 * b,
  ].map((value) => value ** 3);
  return [
    4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s,
    -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s,
    -0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s,
  ];
};

const inGamut = (linear: number[]) =>
  linear.every((value) => value >= 0 && value <= 1);

const encodedChannel = (value: number) => {
  const clamped = Math.min(1, Math.max(0, value));
  const encoded =
    clamped <= 0.0031308
      ? 12.92 * clamped
      : 1.055 * clamped ** (1 / 2.4) - 0.055;
  return Math.round(encoded * 255);
};

// The colour at that oklch lightness, with chroma eased off only where sRGB
// cannot show it, so the hue holds where clipping would shift it.
const atLightness = (lightness: number, a: number, b: number): number[] => {
  let low = 0;
  let high = 1;
  if (inGamut(linearOf([lightness, a, b]))) low = 1;
  while (high - low > CHROMA_PRECISION) {
    const scale = (low + high) / 2;
    if (inGamut(linearOf([lightness, a * scale, b * scale]))) low = scale;
    else high = scale;
  }
  return linearOf([lightness, a * low, b * low]).map(encodedChannel);
};

// The colour itself where it reads, else the lightest step that reaches the
// target: coarse steps find the stretch, fine ones walk it. Black reads on any
// light surface, so the walk ends.
const darkenedUntil = (
  rgb: number[],
  surface: number[],
  target: number
): string => {
  if (contrast(rgb, surface) >= target) return hexOf(rgb);
  const [start, a, b] = oklabOf(rgb);
  const at = (step: number) =>
    atLightness(start - step * OKLAB_LIGHTNESS_STEP, a, b);
  const reads = (step: number) => contrast(at(step), surface) >= target;
  let step = 0;
  while (!reads(step)) step += COARSE_STEPS;
  let fine = Math.max(0, step - COARSE_STEPS + 1);
  while (fine < step && !reads(fine)) fine += 1;
  return hexOf(at(fine));
};

// Undefined leaves olive-50 on its preset.
const backgroundShade = (
  background: string,
  heading: string
): string | undefined => {
  const backgroundHex = opaqueHex(background);
  if (!backgroundHex) return undefined;
  const rgb = rgbOf(backgroundHex);
  const shade =
    luminance(rgb) < DARK_BG_LUMINANCE
      ? mixToward(rgb, 255, BG_SHADE_WHITE_PERCENT)
      : mixToward(rgb, 0, BG_SHADE_BLACK_PERCENT);
  const headingHex = opaqueHex(heading);
  if (headingHex && contrast(shade, rgbOf(headingHex)) < MIN_TEXT_CONTRAST) {
    return 'var(--brand-gray-100)';
  }
  return hexOf(shade);
};

// Empty keeps the darker ramp. Plain hexes, so every engine takes them.
const darkButtonHoverVars = (button: string): Record<string, string> => {
  const buttonHex = opaqueHex(button);
  if (!buttonHex) return {};
  const rgb = rgbOf(buttonHex);
  if (luminance(rgb) >= DARK_BUTTON_LUMINANCE) return {};
  return Object.fromEntries(
    DARK_BUTTON_HOVER_WHITE.map(({ stop, percent }) => [
      `--brand-red-${stop}`,
      hexOf(mixToward(rgb, 255, percent)),
    ])
  );
};

const readsOn = (value: string, background: number[]) => {
  const hex = opaqueHex(value);
  return !!hex && contrast(rgbOf(hex), background) >= MIN_TEXT_CONTRAST;
};

const raisedToward = (
  from: number[],
  startPercent: number,
  background: number[],
  target: number
): string => {
  for (let percent = startPercent; percent < 100; percent += 1) {
    const rgb = mixToward(from, 255, percent);
    if (contrast(rgb, background) >= target) return hexOf(rgb);
  }
  return '#ffffff';
};

type Ground = { link: string; vars: Record<string, string> };

// Null leaves the rule as it was: a light background, one that names no
// colour, or a dark one the studio's own text already reads on. On that last
// one a pale brand still gets the link and its ring, which would otherwise
// fall back to its brand text, a dark colour and often the background itself.
// For the same reason a pale brand's card focus rings get a band in the link
// where the card meets the background. The link is the button where it reads
// on the background, else the heading tier. Powered by gets vars of its own,
// since its hover and focus fall back to the TimiTomi green rather than to a
// tier.
const groundOf = (base: BasePalette, pale: boolean): Ground | null => {
  const backgroundHex = opaqueHex(base.background);
  if (!backgroundHex) return null;
  const background = rgbOf(backgroundHex);
  if (luminance(background) >= DARK_BG_LUMINANCE) return null;
  const failing = GROUND_TIERS.filter(
    ({ tier }) => !readsOn(base[tier], background)
  );
  if (!failing.length && !pale) return null;

  const raised: Partial<Record<GroundTier, string>> = Object.fromEntries(
    failing.map(({ tier, percent, target }) => [
      tier,
      raisedToward(background, percent, background, target),
    ])
  );
  const heading = raised.heading ?? base.heading;
  const link = readsOn(base.button, background) ? base.button : heading;
  const cardRing = pale && { '--ground-card-ring': link };
  if (!failing.length) {
    return {
      link,
      vars: { '--ground-link': link, '--powered-by-ring': link, ...cardRing },
    };
  }

  return {
    link,
    vars: {
      ...Object.fromEntries(
        Object.entries(raised).map(([tier, value]) => [
          `--ground-${tier}`,
          value,
        ])
      ),
      '--ground-link': link,
      '--ground-error': raisedToward(
        GROUND_ERROR,
        0,
        background,
        MIN_TEXT_CONTRAST
      ),
      '--ground-tertiary-label': heading,
      ...Object.fromEntries(
        GROUND_TERTIARY_FILLS.map(({ state, percent }) => [
          `--ground-tertiary-${state}`,
          hexOf(mixToward(background, 255, percent)),
        ])
      ),
      '--powered-by-rest': raised.muted ?? base.muted,
      '--powered-by-hover': heading,
      '--powered-by-ring': link,
      '--powered-by-ring-offset': base.background,
      '--powered-by-gradient': '0',
      ...cardRing,
    },
  };
};

// The studio's gray-150 as the color-mix block paints it.
const brandTextSurface = (muted: string): number[] => {
  const { percent } = GRAY_TINTS.find(
    ({ stop }) => stop === BRAND_TEXT_SURFACE_GRAY
  )!;
  const mutedHex = opaqueHex(muted) ?? opaqueHex(STOCK.muted_text)!;
  return mixToward(rgbOf(mutedHex), 255, 100 - percent);
};

// The studio's dark background where it reads on the surface, since darkened
// lime is a muddy olive, else the button darkened at its hue until it does.
const brandTextOf = (base: BasePalette, button: number[]): string => {
  const surface = brandTextSurface(base.muted);
  const backgroundHex = opaqueHex(base.background);
  if (backgroundHex) {
    const background = rgbOf(backgroundHex);
    if (
      luminance(background) < DARK_BG_LUMINANCE &&
      contrast(background, surface) >= MIN_TEXT_CONTRAST
    ) {
      return `#${backgroundHex}`;
    }
  }
  return darkenedUntil(button, surface, MIN_TEXT_CONTRAST);
};

type Pale = { vars: Record<string, string>; mixed: Record<string, string> };

// Null leaves the rule as it was: a button white reads on, or one that names no
// colour. Hover edges and focus rings take the brand text, since a gray edge
// would match the disabled one, and so do text, edges and rings in the brand
// colour on white (--brand-text-*), while fills keep the button. "Most
// popular" turns into the solid button.
const paleOf = (base: BasePalette): Pale | null => {
  const buttonHex = opaqueHex(base.button);
  if (!buttonHex || !isPaleButton(buttonHex)) return null;
  const button = rgbOf(buttonHex);
  const text = brandTextOf(base, button);
  const deepText = darkenedUntil(
    rgbOf(text.slice(1)),
    WHITE,
    BRAND_TEXT_DEEP_CONTRAST
  );

  return {
    vars: {
      '--mb-on-brand': PALE_INK,
      '--mb-focus-color': text,
      '--brand-text-400': text,
      '--brand-text-500': text,
      '--brand-text-600': text,
      '--brand-text-700': deepText,
      '--brand-text-800': deepText,
      ...Object.fromEntries(
        PALE_HOVER_BLACK.map(({ stop, percent }) => [
          `--brand-red-${stop}`,
          hexOf(mixToward(button, 0, percent)),
        ])
      ),
      ...Object.fromEntries(
        PALE_TINT_GRAYS.map(({ stop, gray }) => [
          `--brand-red-${stop}`,
          `var(--brand-gray-${gray})`,
        ])
      ),
      '--brand-red-300': text,
      '--brand-focus-ring': text,
      '--popular-badge-fill': base.button,
      '--popular-badge-ink': PALE_INK,
    },
    mixed: {
      '--brand-focus-ring': `color-mix(in srgb, ${text} 24%, transparent)`,
    },
  };
};

const palette = (branding: Partial<Branding>): BasePalette => ({
  background: color(branding.background, STOCK.background),
  button: color(branding.button, STOCK.button),
  heading: color(branding.heading_text, STOCK.heading_text),
  body: color(branding.body_text, STOCK.body_text),
  muted: color(branding.muted_text, STOCK.muted_text),
});

const baseVars = (base: BasePalette): Record<string, string> => {
  const shade = backgroundShade(base.background, base.heading);
  return {
    '--brand-bg': base.background,
    ...(shade && { '--brand-bg-shade': shade }),
    '--brand-button': base.button,
    '--brand-heading': base.heading,
    '--brand-body': base.body,
    '--brand-muted': base.muted,
  };
};

const oklchVars = (button: string): Record<string, string> => {
  const vars: Record<string, string> = {};
  for (const { stop, lightness, chroma, hue } of RED_SHADES) {
    vars[`--brand-red-${stop}`] =
      `oklch(from ${button} calc(l - ${lightness}) calc(c * ${chroma}) calc(h + ${hue}))`;
  }
  return vars;
};

const colorMixVars = (base: BasePalette): Record<string, string> => {
  const vars: Record<string, string> = {};
  for (const { stop, percent } of RED_TINTS) {
    vars[`--brand-red-${stop}`] =
      `color-mix(in srgb, ${base.button} ${percent}%, white)`;
  }
  for (const { stop, black } of RED_SHADES) {
    vars[`--brand-red-${stop}`] =
      `color-mix(in srgb, ${base.button} ${100 - black}%, black)`;
  }
  for (const { stop, percent } of GRAY_TINTS) {
    vars[`--brand-gray-${stop}`] =
      `color-mix(in srgb, ${base.muted} ${percent}%, white)`;
  }
  vars['--brand-gray-600'] =
    `color-mix(in srgb, ${base.body} 63%, ${base.muted})`;
  vars['--brand-focus-ring'] =
    `color-mix(in srgb, ${base.button} 24%, transparent)`;
  return vars;
};

// What an engine with neither color-mix() nor relative oklch() keeps: the light
// tints and the dark reds are stock, and the focus ring is the button itself,
// so the ring is solid instead of a 24% halo. The dark reds cannot fall back to
// the button, because the button is a light stop: red-800 text on a red-100
// tint drops from 7.18:1 to 2.31:1, and white on red-500 from 3.76:1 to 2.79:1.
const fallbackVars = (base: BasePalette): Record<string, string> => {
  const vars: Record<string, string> = {};
  for (const { stop, fallback } of RED_TINTS) {
    vars[`--brand-red-${stop}`] = fallback;
  }
  for (const { stop, fallback } of RED_SHADES) {
    vars[`--brand-red-${stop}`] = fallback;
  }
  for (const { stop, fallback } of GRAY_TINTS) {
    vars[`--brand-gray-${stop}`] = fallback;
  }
  vars['--brand-gray-600'] = GRAY_600_FALLBACK;
  vars['--brand-focus-ring'] = base.button;
  return vars;
};

const declarations = (vars: Record<string, string>): string =>
  Object.entries(vars)
    .map(([name, value]) => `${name}:${value}`)
    .join(';');

// A later @supports block would otherwise redeclare a stop the plain block
// already settled, and the last supported one wins.
const withoutVars = (
  vars: Record<string, string>,
  settled: Record<string, string>
): Record<string, string> =>
  Object.fromEntries(
    Object.entries(vars).filter(([name]) => !(name in settled))
  );

// This file owns every branding formula; tailwind.config.ts only reads the
// plain custom properties declared here.
//
// :root rather than a wrapper element: Radix portals mount on document.body, so
// vars scoped to a div inside the tree never reach a modal or a dropdown.
//
// Every derived stop is declared up to three times, least capable engine first:
// a plain value, then color-mix(), then relative oklch() for the dark reds. A
// derived value an engine cannot parse is invalid at computed-value time, which
// resolves the property to unset, so a border would paint transparent and body
// text would inherit whatever sits above it. The var() fallback cannot rescue
// that, since it only applies while the custom property is missing, and by
// then it is defined to an unparseable value. Each @supports block redeclares
// only what its function can express, and the last supported one wins.
//
// A row on a dark background carries .on-ground, which moves the library's
// focus outline onto the ground link: the button outline, or a pale brand's
// text colour, would vanish there.
//
// A pale button's values are plain and settle every stop they name, except its
// focus ring, which the color-mix block still turns into a 24% halo.
export const brandStyleRule = (branding: Partial<Branding>): string => {
  const base = palette(branding);
  const darkHover = darkButtonHoverVars(base.button);
  const pale = paleOf(base);
  const ground = groundOf(base, pale !== null);
  const settled = { ...darkHover, ...pale?.vars };
  const plain = declarations({
    ...baseVars(base),
    ...fallbackVars(base),
    ...darkHover,
    ...LIBRARY_ALIASES,
    ...ground?.vars,
    ...pale?.vars,
  });
  const mixed = declarations({
    ...withoutVars(colorMixVars(base), settled),
    ...pale?.mixed,
  });
  const relative = declarations(withoutVars(oklchVars(base.button), settled));

  const rules = [
    `:root{${plain}}`,
    `@supports (color: color-mix(in srgb, red, white)){:root{${mixed}}}`,
    `@supports (color: oklch(from red l c h)){:root{${relative}}}`,
  ];
  if (ground) rules.push(`.on-ground{--mb-focus-color:${ground.link}}`);
  return rules.join('');
};
